import { Box, Checkbox, Flex, Tag, Text } from "@chakra-ui/react"
import { isEqual } from "lodash"
import { useRouter } from "next/router"
import React, { useState } from "react"
import { encodedSymbols, fhAnalyticsUserActions, keys } from "../../../../../../../common/constants/constants"
import { filterConstants } from "../../../../../../../common/constants/search-constants"
import useKeyPress from "../../../../../../../common/hooks/useKeyPress"
import sendFhAnalytics from "../../../../../../../common/lib/fredhopper"
import { useUrlParams } from "../../../../../../../common/lib/UrlParamsContext"
import { FhResponseData } from "../../../../../../../common/types/search"
import { getContrastColor } from "../../../../../../../common/utils/color-utils"
import {
  getAllCheckboxFiltersDefaultSelectedStatus,
  getCheckboxFilterSeparator,
  getSelectedProductsCount,
} from "../../../../../../../common/utils/search-utils"
import CheckboxIcon from "../../../../../../shared/CheckboxIcon"
import { CCheckmark } from "../../../../../../shared/Icons"
import FilterFormFooter from "../FilterFormFooter/FilterFormFooter"
import FilterZeroResults from "../FilterZeroResults/FilterZeroResults"

type Props = {
  filteredFiltersection: FhResponseData
  searchFiltersection?: FhResponseData // Comes from TypeHeadSearch component, the filteres items
  display_hint: FhResponseData
  url_params_base: FhResponseData
  on: () => void
  isSearchEnabled: boolean
  basetype: FhResponseData
  isOverlayFilters: boolean
  closeFiltersOverlay: () => void
  isColourFilter: boolean
  handleOnClose: () => void
  rid: string
  facetId: string
}

const CheckboxListForm = ({
  filteredFiltersection,
  searchFiltersection,
  display_hint,
  url_params_base,
  on,
  isSearchEnabled,
  basetype,
  isOverlayFilters,
  closeFiltersOverlay,
  isColourFilter,
  handleOnClose,
  rid,
  facetId,
}: Props) => {
  const { setUrlParams } = useUrlParams()
  const { locale } = useRouter()

  const renderedFiltersection = isSearchEnabled ? searchFiltersection : filteredFiltersection
  const defaultAllFiltersSelectedStatus = getAllCheckboxFiltersDefaultSelectedStatus(filteredFiltersection)
  const [allFiltersSelectedStatus, setAllFiltersSelectedStatus] = useState<object>(defaultAllFiltersSelectedStatus)
  const [selectedProductsCount, setSelectedProductsCount] = useState<number>(
    getSelectedProductsCount(renderedFiltersection)
  )
  const [isFormNotTouched, setIsFormNotTouched] = useState<boolean>(true)
  const isAtLeastOneProductSelected = selectedProductsCount > 0
  const isSelectedProductsCountShown = basetype !== filterConstants.SET && isAtLeastOneProductSelected
  const { SLASH, LEFT_CURLY_BRACKET, RIGHT_CURLY_BRACKET, GREATER_THAN } = encodedSymbols
  const separator = getCheckboxFilterSeparator(display_hint)
  const isZeroResults = searchFiltersection?.length === 0

  // Close button is active if form was not touched, nothing is checked and there is no selected from FH response
  const isAtLeastOneFhSelected = renderedFiltersection.some((filter: FhResponseData) => filter.selected)
  const isCloseButtonActive = isFormNotTouched || (selectedProductsCount === 0 && !isAtLeastOneFhSelected)
  // At least one is checked at the moment and it's not zero results
  const isResetAllRendered = isAtLeastOneProductSelected && !isZeroResults

  // this catches all enter cases (search is out of this component and form)
  useKeyPress(keys.ENTER, () => document.getElementById("filter-footer-submit-button").click())

  const checkboxListFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // form is the same when it was opened the first time (same checked and unchecked checkboxes)
    const isFormAllFiltersSelectedStatusNotChanged = isEqual(defaultAllFiltersSelectedStatus, allFiltersSelectedStatus)
    const isOneRemainingCheckbox = searchFiltersection?.length === 1

    // nothing was changed don't make request (covers the case with more than one visible checkbox)
    if (!isOneRemainingCheckbox && isFormAllFiltersSelectedStatusNotChanged) {
      handleOnClose()
      return
    }

    // /series>{ or /type>{ ect
    const categoryInitialsSeparator = SLASH + on + GREATER_THAN + LEFT_CURLY_BRACKET
    let fhDefaultUrl = url_params_base

    // if no /series>{} add it at the end
    if (!fhDefaultUrl.includes(categoryInitialsSeparator)) {
      fhDefaultUrl += categoryInitialsSeparator + RIGHT_CURLY_BRACKET
    }

    const checkedValuesArray = []
    Object.entries(allFiltersSelectedStatus).forEach(([key, value]) => {
      // if value === true (checkbox is checked)
      if (value && !fhDefaultUrl.includes(key)) {
        checkedValuesArray.push(key)
      } else {
        const re = new RegExp(`/${key}${separator}{0,1}/`, "gi")
        fhDefaultUrl.replace(re, "")
      }
    })

    // if only one checkbox add it in checkedValuesArray as if it was manually checked
    if (isOneRemainingCheckbox) {
      const remainingCheckboxValue = searchFiltersection[0].value.value

      // if checkbox is checked and form is in same state when it was opened the first time don't make request
      if (isFormAllFiltersSelectedStatusNotChanged && searchFiltersection[0]?.selected) {
        handleOnClose()
        return
      }

      if (!checkedValuesArray.includes(remainingCheckboxValue)) {
        checkedValuesArray.push(remainingCheckboxValue)
      }
    }

    // split by /series>{
    const [urlLeftPart, urlRightPart] = fhDefaultUrl.split(categoryInitialsSeparator)

    // add values between category curly braces. /series>{ old-values } => /series>{ new-values, old-values }
    // everything before /series>{ + /series>{ + all values + right part starting from }
    fhDefaultUrl = urlLeftPart + categoryInitialsSeparator + checkedValuesArray.join(separator) + urlRightPart

    sendFhAnalytics({
      locale,
      action: fhAnalyticsUserActions.FACETCLICK,
      sourceId: rid,
      facetId,
      facets: {
        prev: defaultAllFiltersSelectedStatus,
        current: allFiltersSelectedStatus,
      },
    })

    // If no checked values left, cleanup the filter section from the url
    if (checkedValuesArray?.length === 0) {
      fhDefaultUrl = fhDefaultUrl.replace(categoryInitialsSeparator + RIGHT_CURLY_BRACKET, "")
    }

    submitAndCloseFilter(fhDefaultUrl)
  }

  // uncheck all checked checkboxes and submit the form
  const resetAndSubmitCheckboxListForm = () => {
    // If there was no default checked checkbox when filter was opened just close the filter (no request)
    if (!Object.values(defaultAllFiltersSelectedStatus).includes(true)) {
      submitAndCloseFilter()
      return
    }

    const fhDefaultUrl = url_params_base

    Object.keys(allFiltersSelectedStatus).forEach((key) => {
      const re = new RegExp(`/${key}${separator}{0,1}/`, "gi")
      fhDefaultUrl.replace(re, "")
    })

    submitAndCloseFilter(fhDefaultUrl)
  }

  const submitAndCloseFilter = (fhDefaultUrl = "") => {
    if (fhDefaultUrl) setUrlParams(fhDefaultUrl)
    handleOnClose()

    // After submit close the overlay
    if (isOverlayFilters) closeFiltersOverlay()
  }

  const filterCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, value: FhResponseData, nr: number) => {
    const isChecked = e.target.checked

    setIsFormNotTouched(false)
    setAllFiltersSelectedStatus({ ...allFiltersSelectedStatus, [value.value]: isChecked })

    if (isChecked) {
      setSelectedProductsCount((prevState) => prevState + nr)
    } else {
      setSelectedProductsCount((prevState) => prevState - nr)
    }
  }

  return (
    <Box width="100%">
      <form onSubmit={checkboxListFormSubmit}>
        <Flex direction="column" gap={isOverlayFilters ? "2" : "0"}>
          {renderedFiltersection.map((filter: FhResponseData) => {
            const { link, value, nr } = filter
            const isChecked = allFiltersSelectedStatus[value.value]
            const highlightColor = getContrastColor(`#${value.value}`)

            return (
              <Flex
                key={JSON.stringify(filter)}
                align="center"
                justify="space-between"
                w="100%"
                h="10"
                pl={isOverlayFilters ? { base: "6", sm: "8" } : "2"}
                pr={isOverlayFilters ? { base: "6", sm: "8" } : "3"}
                cursor="pointer"
                _hover={{
                  "@media (hover: hover) and (pointer: fine)": {
                    bgColor: "gray.200",
                    ".chakra-checkbox__control": {
                      borderColor: "black",
                      bg: isColourFilter ? `#${value.value}` : "gray.200",
                      _checked: {
                        bg: isColourFilter ? `#${value.value}` : "gray.900",
                        borderColor: "black",
                      },
                    },
                  },
                }}
                sx={
                  isColourFilter && {
                    ".chakra-checkbox__control": {
                      bg: `#${value.value}`,
                      _checked: {
                        color: highlightColor,
                        borderColor: value.value !== "ffffff" ? `#${value.value}` : "black",
                        bg: `#${value.value}`,
                      },
                    },
                  }
                }
              >
                <Checkbox
                  isChecked={isChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => filterCheckboxChange(e, value, nr)}
                  value={value.value}
                  name={link.name}
                  id={value.value}
                  flexGrow={1}
                  icon={!isChecked ? <></> : <CheckboxIcon as={CCheckmark} stroke={highlightColor} />}
                >
                  <Flex align="center" justify="space-between" display="inline-flex" w="100%">
                    {link.name}
                    <Tag borderRadius="full" bgColor="gray.200" h="6">
                      <Text color="gray.900" fontSize="xs" fontWeight="medium">
                        {nr}
                      </Text>
                    </Tag>
                  </Flex>
                </Checkbox>
              </Flex>
            )
          })}
        </Flex>

        {isZeroResults && <FilterZeroResults />}

        <FilterFormFooter
          isBoxShadow={renderedFiltersection.length > 7}
          isSelectedProductsCountShown={isSelectedProductsCountShown}
          selectedProductsCount={selectedProductsCount}
          resetAllClickFunction={resetAndSubmitCheckboxListForm}
          isCloseButtonActive={isCloseButtonActive}
          isResetAllRendered={isResetAllRendered}
        />
      </form>
    </Box>
  )
}

export default CheckboxListForm
